import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    __gcse?: {
      searchCallbacks?: {
        web?: {
          ready?: (
            name: string,
            q: string,
            promos: Promo[],
            results: Result[],
            resultsDiv: HTMLElement,
          ) => void;
          rendered?: (
            _gname: string,
            query: string,
            _promoElts: HTMLElement[],
            _resultElts: HTMLElement[],
          ) => void;
        };
      };
      parsetags?: string;
      initializationCallback?: () => void;
    };
    google?: {
      setOnLoadCallback?: (callback: () => void, async?: boolean) => void;
    };
  }
}

interface Promo {
  content: string;
  image: {
    height: number;
    url: string;
    width: number;
  };
  title: string;
  url: string;
  visibleUrl: string;
}

interface Result {
  content: string;
  contentNoFormatting: string;
  contextUrl: string; // For image search results only
  fileFormat: string;
  image: {
    // For image search reseults only
    height: number;
    url: string;
    width: number;
  };
  perResultLabels?: {
    anchor: string;
    label: string;
    labelWithOp: string;
  }[];
  richSnippet?: object[]; // Optional, for web search results only
  thumbnailImage?: {
    height: number;
    url: string;
    width: number;
  };
  title: string;
  titleNoFormatting: string;
  url: string;
  visibleUrl: string;
}

interface GcseSearchProps {
  openDialog: (value: boolean) => void;
}

const GcseSearch: React.FC<GcseSearchProps> = ({ openDialog }) => {
  const router = useRouter();
  const { t } = useTranslation();

  const [isInitialState, setIsInitialState] = useState(true);
  const [resultsLength, setResultsLength] = useState(0);
  const [loading, setLoading] = useState(false);

  const localSearchHistory: string[] = JSON.parse(
    localStorage.getItem('searchHistory') ?? '[]',
  ) as string[];
  const [searchHistory, setSearchHistory] =
    useState<string[]>(localSearchHistory);

  const makeTwoPartCallback = () => {
    const readyCallback = (
      name: string,
      q: string,
      promos: Promo[],
      results: Result[],
      resultsDiv: HTMLElement,
    ) => {
      function createAnchor({
        href = '#',
        text = '',
        target = '_self',
        className = '',
        attributes = {},
        children = [],
      }: {
        href: string;
        text?: string;
        target?: string;
        className: string;
        attributes?: Record<string, string>;
        children?: (HTMLElement | Text)[];
      }) {
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.innerHTML = text;
        anchor.target = target;

        if (className) anchor.className = className;

        Object.entries(attributes).forEach(([key, value]) => {
          if (typeof value === 'string') {
            anchor.setAttribute(key, value);
          }
        });
        children.forEach((child) => {
          anchor.appendChild(child);
        });

        return anchor;
      }
      function createDiv({
        className = '',
        attributes = {},
        children = [],
        text = '',
      }: {
        className?: string;
        attributes?: Record<string, string>;
        children?: (HTMLElement | Text)[];
        text?: string;
      } = {}) {
        const div = document.createElement('div');
        if (className) div.className = className;
        if (text) div.innerHTML = text;

        Object.entries(attributes).forEach(([key, value]) => {
          if (typeof value === 'string') {
            div.setAttribute(key, value);
          }
        });

        children.forEach((child) => {
          div.appendChild(child);
        });

        return div;
      }
      const makePromoElt = (promo: Promo) => {
        const anchor = createAnchor({
          href: promo.url,
          target: '_blank',
          className: 'gs-title',
          text: `Promo: ${promo.title}`,
        });
        return anchor;
      };

      const makeResultElt = (result: Result) => {
        // Create result thumbnail image
        const image = document.createElement('img');
        image.classList.add('gs-image');
        image.src = result.thumbnailImage?.url ?? '';
        image.alt = 'Thumbnail image';

        const imageLink = createAnchor({
          className: 'gs-image',
          href: result.url,
          children: [image],
        });
        const imageBox = createDiv({
          className: 'gs-image-box gs-web-image-box gs-web-image-box-portrait',
          children: [imageLink],
        });

        const thumbnailCell = createDiv({
          className: 'gsc-table-cell-thumbnail gsc-thumbnail',
          children: [imageBox],
        });

        // Create search result title and link
        const titleLink = createAnchor({
          className: 'gs-title',
          href: result.url,
          text: result.title,
          attributes: { dir: 'ltr' },
        });

        const titleContainer = createDiv({
          className: 'gs-title gsc-table-cell-thumbnail gsc-thumbnail-left',
          children: [titleLink],
        });

        const anchor = createAnchor({
          href: result.url,
          className: 'gs-title',
          text: result.title,
        });

        const snippetTitle = createDiv({
          className: 'gs-title',
          children: [anchor],
        });

        // Create search results text snippet
        const snippetText = createDiv({
          attributes: { dir: 'ltr' },
          text: result.content.replace(/.*<b>\.\.\.<\/b>\s*/, ''),
        });

        // Create search results snippet container for title and text snippet
        const snippetContainer = createDiv({
          className: 'gs-bidi-start-align gs-snippet',
          children: [snippetTitle, snippetText],
        });

        const snippetCell = createDiv({
          className: 'gsc-table-cell-snippet-close',
          children: [titleContainer, snippetContainer],
        });

        const tableResult = createDiv({
          className: 'gsc-table-result',
          children: [thumbnailCell, snippetCell],
        });

        const webResult = createDiv({
          className: 'gs-webResult gs-result',
          children: [tableResult],
        });
        const container = createDiv({
          className: 'gsc-webResult gsc-result',
          children: [webResult],
        });

        return container;
      };

      // Add each element to resultsDiv
      if (promos) {
        for (const promo of promos) {
          const promoElement = makePromoElt(promo);
          resultsDiv.appendChild(promoElement);
        }
      }

      if (results) {
        for (const result of results) {
          const resultElement = makeResultElt(result);
          resultsDiv.appendChild(resultElement);
        }
      }

      return true;
    };
    const renderedCallback = (
      _gname: string,
      query: string,
      _promoElts: HTMLElement[],
      _resultElts: HTMLElement[],
    ) => {
      saveHistory(query);

      const resultInfo = document.getElementById('resInfo-0');
      const resultString = resultInfo?.innerText;
      const regex = /About (\d+) results/;
      const match = regex.exec(resultString ?? '');

      if (match && match.length > 1) {
        const number = match[1];
        if (number) {
          setResultsLength(parseInt(number));
        }
      } else {
        setResultsLength(0);
      }
      setIsInitialState(false);
      setLoading(false);
    };
    return { readyCallback, renderedCallback };
  };

  useEffect(() => {
    const script = document.createElement('script');
    document.head.appendChild(script);
    script.src = 'https://cse.google.com/cse.js?cx=326183205f5cb4f01';

    const gapi = document.createElement('script');
    document.head.appendChild(gapi);
    gapi.src = 'https://www.google.com/jsapi';

    window.__gcse ??
      (window.__gcse = {
        parsetags: 'onload',
        initializationCallback: initialCallback,
      });

    const {
      readyCallback: webResultsReadyCallback,
      renderedCallback: webResultsRenderedCallback,
    } = makeTwoPartCallback();
    window.__gcse = window.__gcse ?? {};
    window.__gcse.searchCallbacks = {
      web: {
        ready: webResultsReadyCallback,
        rendered: webResultsRenderedCallback,
      },
    };
  }, []);

  const initialCallback = function () {
    if (document.readyState == 'complete') {
      attachListeners();
    } else {
      if (window.google?.setOnLoadCallback) {
        window.google.setOnLoadCallback(function () {
          attachListeners();
        }, true);
      }
    }
  };

  const attachListeners = () => {
    hideOrderByContainer();
    onInputTextBoxListener();
    searchOnClickListener();
    inputBoxListener();
    checkInputValue();
    clearTextBoxListener();
    resultsOnClickListener();
  };

  const checkInputValue = () => {
    const textboxElement = document.getElementById(
      'gsc-i-id1',
    ) as HTMLInputElement;
    if (textboxElement?.value?.includes('+')) {
      textboxElement.value = textboxElement.value.replace(/\+/g, ' ');
    }
  };

  const hideOrderByContainer = () => {
    const orderByContainers = document.getElementsByClassName(
      'gsc-orderby-container',
    );

    if (orderByContainers && orderByContainers.length > 0) {
      const orderByContainer = orderByContainers[0] as HTMLElement;
      orderByContainer.style.visibility = 'hidden';
    }
  };

  const onInputTextBoxListener = () => {
    const textboxElement = document.getElementById(
      'gsc-i-id1',
    ) as HTMLInputElement;

    // attach listener to clear search text button as it is rerendered on search text change
    textboxElement?.addEventListener('input', function (event) {
      const target = event.target as HTMLInputElement;
      const searchText = target.value;
      if (searchText !== '') {
        clearTextBoxListener();
      }
    });

    // clear "no results" prompt on enter
    textboxElement?.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' && textboxElement.value.trim() !== '') {
        setLoading(true);
      }
    });
  };

  const clearTextBoxListener = () => {
    const cancelButton = document.getElementsByClassName('gsst_a')[0];

    // skip adding event listener when exist
    if (cancelButton?.getAttribute('listener') !== 'true') {
      cancelButton?.addEventListener('click', function (e) {
        const elementClicked = e.target;
        if (elementClicked instanceof HTMLElement) {
          elementClicked.setAttribute('listener', 'true');
          setIsInitialState(true);
        }
      });
    }
  };

  const searchOnClickListener = () => {
    const searchButtonElement =
      document.getElementsByClassName('gsc-search-button')[0];

    const textboxElement = document.getElementById(
      'gsc-i-id1',
    ) as HTMLInputElement;

    searchButtonElement?.addEventListener('click', function () {
      if (textboxElement.value.trim() !== '') {
        setLoading(true);
        setTimeout(() => {
          const recaptchaContainer =
            document.getElementById('recaptcha-wrapper');
          // show results container when recaptcha verification required
          if (recaptchaContainer) {
            setResultsLength(1);
            return;
          }
        }, 200);
        setIsInitialState(false);
      }
    });
  };

  const inputBoxListener = () => {
    const inputBox = document.getElementById('gsc-i-id1');

    // add and remove border when using clicks on search box
    inputBox?.addEventListener('focus', function () {
      const inputBoxContainer = document.getElementsByClassName(
        'gsc-input-box',
      )[0] as HTMLElement;
      inputBoxContainer.style.border = '1px solid #00a6fb';
    });

    inputBox?.addEventListener('blur', function () {
      const inputBoxContainer = document.getElementsByClassName(
        'gsc-input-box',
      )[0] as HTMLElement;
      inputBoxContainer.style.border = '1px solid #e1e8eb';
    });
  };

  const resultsOnClickListener = () => {
    document.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement | null)?.closest(
        'a',
      ) as HTMLAnchorElement | null;
      if (target) {
        sessionStorage.removeItem('isDialogOpen');
      }
    });
  };

  const saveHistory = (text: string) => {
    if (!text) return;

    const searchHistroyArr: string[] = JSON.parse(
      localStorage.getItem('searchHistory') ?? '[]',
    ) as string[];

    searchHistroyArr.push(text);

    const excludedDuplicates = [...new Set(searchHistroyArr)];
    setSearchHistory(excludedDuplicates);

    localStorage.setItem('searchHistory', JSON.stringify(excludedDuplicates));
  };

  const deleteHistory = () => {
    localStorage.removeItem('searchHistory');
    setSearchHistory([]);
  };

  const applySearchText = (searchText: string) => {
    const currentUrl = window.location.href;
    const queryParams = new URLSearchParams({
      'gsc.q': searchText,
    });

    if (!currentUrl.includes('#gsc.tab=0&')) {
      queryParams.append('gsc.tab', '0');
    }

    if (!currentUrl.includes('&gsc.sort=')) {
      queryParams.append('gsc.sort', 'date');
    }

    const newUrl = `${currentUrl.split('#')[0]}#${queryParams.toString()}`;

    const navigate = async () => {
      await router.push(newUrl, undefined, {
        shallow: true,
      });
    };

    void navigate();

    setTimeout(() => {
      window.location.reload();
      sessionStorage.setItem('isDialogOpen', 'true');
    }, 100);

    setTimeout(() => {
      const recaptchaContainer = document.getElementById('recaptcha-wrapper');
      // show results container when recaptcha verification required
      if (recaptchaContainer) {
        setResultsLength(1);
        return;
      }
    }, 200);
  };

  const renderLastSearch = () => {
    return (
      <div className="text-base font-normal lg:max-w-[60%]">
        <div className="text-lightGrey mb-2">{t('lastSearch')}</div>

        {searchHistory.map((search, i) => (
          <button
            key={`${search}_${i}`}
            className="text-darkGrey mr-2 mt-4 inline-flex cursor-pointer rounded-3xl border border-gray-200 px-4 text-center leading-10"
            onClick={() => applySearchText(search)}
          >
            {search}
          </button>
        ))}
        <button
          className="text-darkGrey my-6 block w-fit cursor-pointer underline"
          onClick={deleteHistory}
        >
          {t('clearAll')}
        </button>
      </div>
    );
  };

  return (
    <div className="google-search-container flex flex-col">
      <div className="mb-1 flex flex-row-reverse">
        <button
          className="cursor-pointer rounded-3xl border-2 border-[#00A6FB] px-4 py-2 text-[#00A6FB] "
          onClick={() => openDialog(false)}
        >
          {t('closeSearch')} &ensp;X
        </button>
      </div>
      <div className={isInitialState ? 'my-auto' : ''}>
        <div className="mx-auto max-w-lg">
          {isInitialState && (
            <div
              className={`no-results-header text-brandAlt-950 p-4 ${
                isInitialState ? '' : 'hidden'
              }`}
            >
              {t('pleaseTypeSearch')}
            </div>
          )}
          <div className="pb-4">
            <div className="gcse-searchbox"></div>
          </div>
          {searchHistory && searchHistory.length > 0
            ? renderLastSearch()
            : null}
        </div>
        <div className={resultsLength === 0 || isInitialState ? 'hidden' : ''}>
          <div className="gcse-searchresults" data-sort_by="date"></div>
        </div>
        {isInitialState && (
          <div
            className={`flex flex-1 flex-col items-center p-8 ${
              searchHistory?.length > 0 ? 'lg:p-10' : 'lg:p-20'
            }`}
          ></div>
        )}
        {loading === false && resultsLength === 0 && !isInitialState && (
          <div
            className={`flex flex-1 flex-col items-center p-8 ${
              searchHistory?.length > 0 ? 'lg:p-10' : 'lg:p-20'
            }`}
          >
            <div className="no-results-header text-brandAlt-950 p-4">
              {t('noSearchResults')}
            </div>
            <div className="text-darkGrey pb-4">{t('tryAnotherKeyword')}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GcseSearch;
